/*
  Promo bar — re-picks the banner with the browser clock, ticks countdowns,
  swaps at the end (or the next scheduled start) without a reload, and rotates
  normal banners. Selection rule matches sections/promo-bar.liquid and
  ClaymooApp's shared/store-content-pick.ts: the running scheduled banner with
  the lowest position, else the normal banners by position.
*/
if (!customElements.get('promo-bar')) {
  customElements.define(
    'promo-bar',
    class PromoBar extends HTMLElement {
      connectedCallback() {
        this.banners = PromoBar.byPosition(Array.from(this.querySelectorAll('.promo-bar__banner')));
        // A preview copy's clock starts at its "Preview at" moment and runs on from there.
        const previewAt = Number(this.dataset.previewAt);
        this.offset = previewAt ? previewAt - Date.now() / 1000 : 0;
        this.rotateMs = (Number(this.dataset.rotate) || 3) * 1000;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.paused = false;
        this.addEventListener('mouseenter', () => (this.paused = true));
        this.addEventListener('mouseleave', () => (this.paused = false));
        this.addEventListener('focusin', () => (this.paused = true));
        this.addEventListener('focusout', () => (this.paused = false));
        this.render();
      }

      now() {
        return Date.now() / 1000 + this.offset;
      }

      // Lowest position first; no position goes last; ties keep page order.
      static byPosition(banners) {
        const rank = (el) => (el.dataset.position === '' || el.dataset.position === undefined ? Infinity : Number(el.dataset.position));
        return banners.map((el, i) => ({ el, i })).sort((a, b) => rank(a.el) - rank(b.el) || a.i - b.i).map((x) => x.el);
      }

      disconnectedCallback() {
        this.stop();
      }

      stop() {
        clearInterval(this.tickTimer);
        clearInterval(this.rotateTimer);
        clearTimeout(this.nextTimer);
      }

      static seconds(value, fallback) {
        return value === '' || value === undefined ? fallback : Number(value);
      }

      pick(now) {
        const scheduled = [];
        const normal = [];
        this.banners.forEach((el) => {
          if (el.dataset.start === '' && el.dataset.end === '') normal.push(el);
          else scheduled.push(el);
        });
        const active = scheduled.find((el) => {
          const start = PromoBar.seconds(el.dataset.start, -Infinity);
          const end = PromoBar.seconds(el.dataset.end, Infinity);
          return start <= now && now < end;
        });
        // Next moment the pick can change: the active banner's end, or any later start.
        let next = Infinity;
        if (active) next = PromoBar.seconds(active.dataset.end, Infinity);
        scheduled.forEach((el) => {
          const start = PromoBar.seconds(el.dataset.start, -Infinity);
          if (start > now) next = Math.min(next, start);
        });
        return { shown: active ? [active] : normal, next };
      }

      render() {
        this.stop();
        const now = this.now();
        const { shown, next } = this.pick(now);

        this.banners.forEach((el) => (el.hidden = !shown.includes(el)));
        this.hidden = shown.length === 0;

        if (shown.length === 1 && shown[0].dataset.style === 'countdown') this.tick(shown[0]);
        if (shown.length > 1) this.rotate(shown);

        // setTimeout caps at ~24.8 days; re-check at least that often.
        if (next !== Infinity) {
          const ms = Math.min((next - now) * 1000 + 250, 2147483647);
          this.nextTimer = setTimeout(() => this.render(), ms);
        }
      }

      tick(banner) {
        const end = PromoBar.seconds(banner.dataset.end, Infinity);
        if (end === Infinity) return;
        const digits = {};
        banner.querySelectorAll('[data-unit]').forEach((el) => (digits[el.dataset.unit] = el));
        const pad = (n) => String(n).padStart(2, '0');
        const update = () => {
          const now = this.now();
          // Re-pick only once the end has passed, so pick() can't choose this banner again.
          if (now >= end) return this.render();
          const left = Math.ceil(end - now);
          if (digits.d) digits.d.textContent = pad(Math.floor(left / 86400));
          if (digits.h) digits.h.textContent = pad(Math.floor((left % 86400) / 3600));
          if (digits.m) digits.m.textContent = pad(Math.floor((left % 3600) / 60));
          if (digits.s) digits.s.textContent = pad(left % 60);
        };
        update();
        this.tickTimer = setInterval(update, 1000);
      }

      rotate(shown) {
        let index = 0;
        shown.forEach((el, i) => (el.hidden = i !== 0));
        if (this.reducedMotion) return;
        this.rotateTimer = setInterval(() => {
          if (this.paused) return;
          shown[index].hidden = true;
          index = (index + 1) % shown.length;
          shown[index].hidden = false;
        }, this.rotateMs);
      }
    }
  );
}
